import { css } from "@compiled/react";
import { useIntl, MessageDescriptor } from "react-intl";

import ArrowRightIcon from "@atlaskit/icon/glyph/arrow-right";
import { Show } from "@atlaskit/primitives/responsive";
import { media } from "@atlaskit/primitives/responsive";

const rightHandImageDesktopUrl =
    "https://wac-cdn.atlassian.com/misc-assets/webp-images/team-24-ad-banner-confluence-right-hand-image-desktop.webp-3380811166.webp";

const rightHandImageTabletUrl =
    "https://wac-cdn.atlassian.com/misc-assets/webp-images/team-24-ad-banner-confluence-right-hand-image-tablet.webp-2937812902.webp";

const rightHandImageDesktopUrlJira =
    "https://wac-cdn.atlassian.com/misc-assets/webp-images/team-24-anu-desktop.webp";

const rightHandImageTabletUrlJira =
    "https://wac-cdn.atlassian.com/misc-assets/webp-images/team-24-anu-tablet.webp";

const confluenceBannerContainerStyles = css({
    display: "flex",
    flexDirection: "row",
    width: "1169px",
    height: "141px",
    borderRadius: "4px",
    backgroundColor: "var(--Primary-Blue-700, #1868DB)",
    backgroundImage: `url(${rightHandImageDesktopUrl})`,
    backgroundSize: "196px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 70px center",
    [media.below.sm]: {
        display: "flex",
        width: "343px",
        height: "316px",
        padding: "28px 16px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "32px",
        backgroundImage: "unset"
    },
    [media.only.sm]: {
        display: "flex",
        width: "736px",
        height: "224px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${rightHandImageTabletUrl})`,
        backgroundSize: "144px",
        backgroundPosition: "right"
    },
    [media.only.md]: {
        display: "flex",
        width: "736px",
        height: "224px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${rightHandImageTabletUrl})`,
        backgroundSize: "144px",
        backgroundPosition: "right"
    }
});

const jiraBannerContainerStyles = css({
    display: "flex",
    flexDirection: "row",
    width: "1169px",
    height: "141px",
    borderRadius: "4px",
    backgroundColor: "var(--Primary-Blue-700, #1868DB)",
    backgroundImage: `url(${rightHandImageDesktopUrlJira})`,
    backgroundSize: "266px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 0px center",
    [media.below.sm]: {
        display: "flex",
        width: "343px",
        height: "316px",
        padding: "28px 16px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "32px",
        backgroundImage: "unset"
    },
    [media.only.sm]: {
        display: "flex",
        width: "736px",
        height: "224px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: "none",
        backgroundSize: "226px",
        backgroundPosition: "right"
    },
    [media.only.md]: {
        display: "flex",
        width: "900px",
        height: "224px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${rightHandImageTabletUrlJira})`,
        backgroundSize: "226px",
        backgroundPosition: "right",
        paddingInlineEnd: "200px"
    }
});

const contentContainerStyles = css({
    height: "141px",
    display: "inline-flex",
    alignItems: "center",
    gap: "50px",
    padding: "37px 0 27px 48px",
    [media.below.sm]: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "28px 16px",
        gap: "0"
    },
    [media.only.sm]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "space-around",
        justifyContent: "center",
        height: "56px",
        width: "100%",
        padding: "0",
        gap: "0"
    },
    [media.only.md]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "space-around",
        justifyContent: "center",
        height: "56px",
        width: "100%",
        padding: "0",
        gap: "0"
    }
});

const leftHandImageStyles = css({
    width: "200px",
    height: "77px",
    [media.below.lg]: {
        display: "none"
    }
});

const textContentStyles = css({
    width: "393px",
    height: "77px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    [media.below.sm]: {
        justifyContent: "center",
        alignItems: "center",
        width: "343px",
        height: "100%",
        paddingBlockEnd: "16px"
    },
    [media.only.sm]: {
        justifyContent: "center",
        alignItems: "center",
        height: "144px",
        width: "100%",
        paddingBlockEnd: "16px"
    },
    [media.only.md]: {
        justifyContent: "center",
        alignItems: "center",
        height: "144px",
        width: "100%",
        paddingBlockEnd: "16px"
    }
});

const titleStyles = css({
    color: `var(--Primary-White, #FFF)`,
    fontFamily: "Charlie Text",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "20px",
    [media.below.sm]: {
        width: "270px",
        fontSize: "24px",
        fontStyle: "normal",
        fontWeight: "700",
        lineHeight: "32px",
        letterSpacing: "0.3px",
        textAlign: "center",
        paddingBlockEnd: "16px"
    },
    [media.only.sm]: {
        width: "100%",
        fontSize: "24px",
        fontStyle: "normal",
        fontWeight: "700",
        lineHeight: "32px",
        letterSpacing: "0.3px",
        textAlign: "center",
        paddingBlockEnd: "16px"
    },
    [media.only.md]: {
        width: "100%",
        fontSize: "24px",
        fontStyle: "normal",
        fontWeight: "700",
        lineHeight: "32px",
        letterSpacing: "0.3px",
        textAlign: "center",
        paddingBlockEnd: "16px"
    }
});

const paragraphStyles = css({
    color: `var(--Primary-White, #FFF)`,
    fontFamily: "Charlie Text",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
    [media.below.sm]: {
        width: "255px",
        fontSize: "20px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "24px",
        letterSpacing: "0.3px",
        textAlign: "center"
    },
    [media.only.sm]: {
        width: "523px",
        fontSize: "20px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "28px",
        letterSpacing: "0.3px",
        textAlign: "center"
    },
    [media.only.md]: {
        width: "523px",
        fontSize: "20px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "28px",
        letterSpacing: "0.3px",
        textAlign: "center"
    }
});

const callToActionStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: `var(--Primary-White, #FFF)`,
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: "Charlie Text",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "20px",
    [media.below.sm]: {
        fontSize: "18px",
        fontStyle: "normal",
        fontWeight: "600"
    },
    [media.only.sm]: {
        fontSize: "18px",
        fontStyle: "normal",
        fontWeight: "600"
    },
    [media.only.md]: {
        fontSize: "18px",
        fontStyle: "normal",
        fontWeight: "600"
    }
});

const callToActionIconStyles = css({
    width: "11px",
    height: "8px",
    [media.below.sm]: {},
    [media.only.sm]: {
        width: "24px",
        height: "24px"
    },
    [media.only.md]: {
        width: "24px",
        height: "24px"
    }
});

export type Team24AdBannerProps = {
    readonly team24LogoAlt: MessageDescriptor;
    readonly team24LogoDesktopUrl: string;
    readonly adCopyTitle: MessageDescriptor;
    readonly adCopyParagraph: MessageDescriptor;
    readonly callToActionUrl: string;
    readonly callToActionText: MessageDescriptor;
    readonly callToActionIconLabel: MessageDescriptor;
    readonly onClickFunc?: () => void;
    readonly backgroundStyle?: "confluence" | "jira";
};

export const Team24AdBanner = ({
    team24LogoAlt,
    team24LogoDesktopUrl,
    adCopyTitle,
    adCopyParagraph,
    callToActionUrl,
    callToActionText,
    callToActionIconLabel,
    onClickFunc,
    backgroundStyle = "confluence"
}: Team24AdBannerProps) => {
    const intl = useIntl();

    return (
        <div
            css={[
                backgroundStyle === "confluence"
                    ? confluenceBannerContainerStyles
                    : jiraBannerContainerStyles
            ]}
        >
            <div css={contentContainerStyles}>
                <img
                    css={leftHandImageStyles}
                    src={team24LogoDesktopUrl}
                    alt={intl.formatMessage(team24LogoAlt)}
                />
                <div css={textContentStyles}>
                    <div css={titleStyles}>
                        {intl.formatMessage(adCopyTitle)}
                    </div>
                    <div css={paragraphStyles}>
                        {intl.formatMessage(adCopyParagraph)}
                    </div>
                </div>
                <div>
                    <a
                        css={callToActionStyles}
                        href={callToActionUrl}
                        onClick={onClickFunc}
                    >
                        {intl.formatMessage(callToActionText)}
                        <Show above={"lg"}>
                            <ArrowRightIcon
                                size={"small"}
                                css={callToActionIconStyles}
                                label={intl.formatMessage(
                                    callToActionIconLabel
                                )}
                            />
                        </Show>
                        <Show below={"lg"}>
                            <ArrowRightIcon
                                size={"medium"}
                                css={callToActionIconStyles}
                                label={intl.formatMessage(
                                    callToActionIconLabel
                                )}
                            />
                        </Show>
                    </a>
                </div>
            </div>
        </div>
    );
};
